import { useState } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { formatDate } from '@/utils/content';
import { news } from '@/mocks/news';

const step = 4;

export default function News() {
  const [visible, setVisible] = useState(step);

  if (news.length === 0) {
    return (
      <PageLayout>
        <div className="mx-auto flex w-full max-w-3xl flex-col items-center justify-center px-4 py-28 text-center">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-newspaper-line text-4xl" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">暂无最新影视资讯，敬请期待</h1>
          <Link
            to="/"
            className="mt-8 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            回到首页
          </Link>
        </div>
      </PageLayout>
    );
  }

  const [lead, ...rest] = news;
  const list = rest.slice(0, visible);
  const hasMore = visible < rest.length;

  return (
    <PageLayout>
      <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '资讯专题' }]} />

        <div className="mt-6 overflow-hidden rounded-lg bg-foreground-950">
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="relative h-60 w-full md:h-full md:min-h-[320px]">
              <img
                src={lead.cover}
                alt={lead.title}
                title={`${lead.title} - 红杏影视资讯`}
                className="h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 to-transparent md:bg-gradient-to-r" />
            </div>
            <div className="flex flex-col justify-center p-6 md:p-10">
              <span className="inline-block w-fit rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                专题头条 · {lead.category}
              </span>
              <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-background-50 md:text-3xl">
                {lead.title}
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-foreground-300">{lead.excerpt}</p>
              <Link
                to={`/news/${lead.id}`}
                className="mt-6 inline-flex w-fit cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-foreground-950 transition-colors hover:bg-accent-400"
              >
                阅读全文
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-8 lg:flex-row">
          <div className="min-w-0 flex-1">
            <h2 className="mb-4 font-heading text-xl font-bold text-foreground-950">资讯文章列表</h2>
            <div className="flex flex-col gap-4">
              {list.map((article) => (
                <article
                  key={article.id}
                  className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200 sm:flex-row"
                >
                  <div className="h-48 w-full shrink-0 overflow-hidden bg-background-200 sm:h-auto sm:w-56">
                    <img
                      src={article.cover}
                      alt={article.title}
                      title={`${article.title} - 红杏影视资讯`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col justify-center p-5">
                    <div className="flex items-center gap-2">
                      <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900">
                        {article.category}
                      </span>
                      <span className="text-xs text-foreground-500">{formatDate(article.date)}</span>
                    </div>
                    <Link to={`/news/${article.id}`} className="mt-2 cursor-pointer">
                      <h3 className="font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                        {article.title}
                      </h3>
                    </Link>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                      {article.excerpt}
                    </p>
                    <div className="mt-3 flex items-center gap-4 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line" />
                        {article.author}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line" />
                        {article.views.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {hasMore ? (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + step)}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-8 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-arrow-down-line" />
                  加载更多资讯
                </button>
              </div>
            ) : (
              <p className="mt-8 text-center text-sm text-foreground-400">已经到底啦，更多精彩内容持续更新中</p>
            )}
          </div>

          <aside className="w-full shrink-0 lg:w-72">
            <div className="sticky top-24 flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading text-base font-semibold text-foreground-950">热门资讯</h3>
                <div className="mt-4 flex flex-col gap-4">
                  {[...news]
                    .sort((a, b) => b.views - a.views)
                    .slice(0, 5)
                    .map((article, index) => (
                      <Link
                        key={article.id}
                        to={`/news/${article.id}`}
                        className="group flex cursor-pointer items-start gap-3"
                      >
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded text-xs font-bold ${
                            index < 3
                              ? 'bg-primary-500 text-background-50'
                              : 'bg-background-200 text-foreground-600'
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="line-clamp-2 text-sm leading-snug text-foreground-700 transition-colors group-hover:text-primary-600">
                          {article.title}
                        </span>
                      </Link>
                    ))}
                </div>
              </div>

              <div className="rounded-lg bg-primary-50 p-5">
                <h3 className="font-heading text-base font-semibold text-primary-800">投稿与线索</h3>
                <p className="mt-2 text-sm leading-relaxed text-primary-700/80">
                  有想推荐的好片或新闻线索？欢迎通过联系表单告诉我们，一起丰富影视内容。
                </p>
                <Link
                  to="/about"
                  className="mt-4 inline-flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-700 hover:text-primary-800"
                >
                  前往反馈
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </aside>
        </div>

        <section className="mt-12">
          <h2 className="mb-4 font-heading text-xl font-bold text-foreground-950">相关文章推荐</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {news.slice(0, 3).map((article) => (
              <Link
                key={`related-${article.id}`}
                to={`/news/${article.id}`}
                className="group flex cursor-pointer gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-200"
              >
                <div className="h-16 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img src={article.cover} alt={article.title} className="h-full w-full object-cover object-top" />
                </div>
                <div className="min-w-0">
                  <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-600">
                    {article.title}
                  </h3>
                  <p className="mt-1 text-xs text-foreground-500">{formatDate(article.date)}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </PageLayout>
  );
}